import { useState, useCallback } from 'react';
import type { ResumeData, ResumeSettings, TemplateType, TailorResult } from '../types/resume';

const emptyResume: ResumeData = {
  personal: {
    fullName: '',
    email: '',
    phone: '',
    location: '',
    website: '',
    linkedin: '',
    summary: '',
  },
  experience: [],
  education: [],
  skills: [],
  projects: [],
  certifications: [],
};

const defaultSettings: ResumeSettings = {
  format: 'A4',
  margins: { top: 0.3, bottom: 0.3, left: 0.3, right: 0.3 },
  spacing: { sectionGap: 10, titleGap: 4, contentGap: 6, lineSpacing: 1.5 },
  fonts: { primary: 'Inter', secondary: 'Inter' },
  sizes: { heading: 100, body: 100 },
  dateFormat: 'short',
  headerAlignment: 'left',
  sectionOrder: ['summary', 'experience', 'education', 'skills', 'projects', 'certifications'],
};

export function useResume() {
  const [data, setData] = useState<ResumeData>(emptyResume);
  const [template, setTemplate] = useState<TemplateType>('modern');
  const [settings, setSettings] = useState<ResumeSettings>(defaultSettings);
  const [activeSection, setActiveSection] = useState<string>('personal');

  const updatePersonal = useCallback((field: keyof ResumeData['personal'], value: string) => {
    setData((prev) => ({
      ...prev,
      personal: { ...prev.personal, [field]: value },
    }));
  }, []);

  const addExperience = useCallback(() => {
    setData((prev) => ({
      ...prev,
      experience: [
        ...prev.experience,
        {
          id: crypto.randomUUID(),
          company: '',
          title: '',
          location: '',
          startDate: '',
          endDate: '',
          current: false,
          description: '',
        },
      ],
    }));
  }, []);

  const updateExperience = useCallback((id: string, field: keyof ResumeData['experience'][0], value: any) => {
    setData((prev) => ({
      ...prev,
      experience: prev.experience.map((e) =>
        e.id === id ? { ...e, [field]: value } : e
      ),
    }));
  }, []);

  const removeExperience = useCallback((id: string) => {
    setData((prev) => ({
      ...prev,
      experience: prev.experience.filter((e) => e.id !== id),
    }));
  }, []);

  const addEducation = useCallback(() => {
    setData((prev) => ({
      ...prev,
      education: [
        ...prev.education,
        {
          id: crypto.randomUUID(),
          school: '',
          degree: '',
          field: '',
          location: '',
          startDate: '',
          endDate: '',
          description: '',
        },
      ],
    }));
  }, []);

  const updateEducation = useCallback((id: string, field: keyof ResumeData['education'][0], value: any) => {
    setData((prev) => ({
      ...prev,
      education: prev.education.map((e) =>
        e.id === id ? { ...e, [field]: value } : e
      ),
    }));
  }, []);

  const removeEducation = useCallback((id: string) => {
    setData((prev) => ({
      ...prev,
      education: prev.education.filter((e) => e.id !== id),
    }));
  }, []);

  const addSkill = useCallback(() => {
    setData((prev) => ({
      ...prev,
      skills: [
        ...prev.skills,
        { id: crypto.randomUUID(), name: '' },
      ],
    }));
  }, []);

  const addSkills = useCallback((names: string[]) => {
    const trimmed = names.map((n) => n.trim()).filter(Boolean);
    if (trimmed.length === 0) return;
    setData((prev) => ({
      ...prev,
      skills: [
        ...prev.skills,
        ...trimmed.map((name) => ({ id: crypto.randomUUID(), name })),
      ],
    }));
  }, []);

  const updateSkill = useCallback((id: string, field: keyof ResumeData['skills'][0], value: any) => {
    setData((prev) => ({
      ...prev,
      skills: prev.skills.map((s) =>
        s.id === id ? { ...s, [field]: value } : s
      ),
    }));
  }, []);

  const removeSkill = useCallback((id: string) => {
    setData((prev) => ({
      ...prev,
      skills: prev.skills.filter((s) => s.id !== id),
    }));
  }, []);

  const addProject = useCallback(() => {
    setData((prev) => ({
      ...prev,
      projects: [
        ...prev.projects,
        {
          id: crypto.randomUUID(),
          name: '',
          description: '',
          url: '',
          technologies: [],
        },
      ],
    }));
  }, []);

  const updateProject = useCallback((id: string, field: keyof ResumeData['projects'][0], value: any) => {
    setData((prev) => ({
      ...prev,
      projects: prev.projects.map((p) =>
        p.id === id ? { ...p, [field]: value } : p
      ),
    }));
  }, []);

  const removeProject = useCallback((id: string) => {
    setData((prev) => ({
      ...prev,
      projects: prev.projects.filter((p) => p.id !== id),
    }));
  }, []);

  const addCertification = useCallback(() => {
    setData((prev) => ({
      ...prev,
      certifications: [
        ...prev.certifications,
        {
          id: crypto.randomUUID(),
          name: '',
          issuer: '',
          date: '',
          url: '',
        },
      ],
    }));
  }, []);

  const updateCertification = useCallback((id: string, field: keyof ResumeData['certifications'][0], value: any) => {
    setData((prev) => ({
      ...prev,
      certifications: prev.certifications.map((c) =>
        c.id === id ? { ...c, [field]: value } : c
      ),
    }));
  }, []);

  const removeCertification = useCallback((id: string) => {
    setData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((c) => c.id !== id),
    }));
  }, []);



  const updateSettings = useCallback((updates: Partial<ResumeSettings>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateSectionOrder = useCallback((sectionOrder: string[]) => {
    setSettings((prev) => ({ ...prev, sectionOrder }));
  }, []);

  const setPhoto = useCallback((photo?: string) => {
    setSettings((prev) => ({ ...prev, photo }));
  }, []);

  const applyTailorSuggestions = useCallback((suggestions: TailorResult) => {
    setData((prev) => {
      const next: ResumeData = {
        ...prev,
        personal: {
          ...prev.personal,
          summary: suggestions.tailoredSummary || prev.personal.summary,
        },
        experience: prev.experience.map((exp) => {
          const update = suggestions.suggestedExperience.find((s) => s.id === exp.id);
          return update ? { ...exp, description: update.description } : exp;
        }),
      };

      if (suggestions.suggestedSkills.length) {
        const existing = new Set(prev.skills.map((s) => s.name.toLowerCase()));
        const newSkills = suggestions.suggestedSkills
          .filter((name) => !existing.has(name.toLowerCase()))
          .map((name) => ({ id: crypto.randomUUID(), name }));
        next.skills = [...prev.skills, ...newSkills];
      }

      return next;
    });
  }, []);

  const updateSummary = useCallback((summary: string) => {
    setData((prev) => ({
      ...prev,
      personal: { ...prev.personal, summary },
    }));
  }, []);

  const setResumeData = useCallback((newData: ResumeData) => {
    setData(newData);
  }, []);

  return {
    data,
    template,
    setTemplate,
    activeSection,
    setActiveSection,
    updatePersonal,
    addExperience,
    updateExperience,
    removeExperience,
    addEducation,
    updateEducation,
    removeEducation,
    addSkill,
    addSkills,
    updateSkill,
    removeSkill,
    addProject,
    updateProject,
    removeProject,
    addCertification,
    updateCertification,
    removeCertification,
    setResumeData,
    settings,
    updateSettings,
    updateSectionOrder,
    setPhoto,
    applyTailorSuggestions,
    updateSummary,
  };
}
